import { el } from "../utils/dom";

let host: HTMLElement | null = null;
let closeCurrent: (() => void) | null = null;

/** Mount the single global detail-modal host. Call once from the app shell. */
export function mountModalHost(container: HTMLElement): void {
  host = el("div", { id: "modal-host" });
  container.append(host);
}

export interface OpenModalOptions {
  title: string;
}

/** Collect all tabbable elements within a container (excludes disabled). */
function getFocusable(container: HTMLElement): HTMLElement[] {
  return Array.from(
    container.querySelectorAll<HTMLElement>(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    )
  ).filter((node) => !node.hasAttribute("disabled"));
}

/** Open the read-only detail modal (run details, one-time key reveal, etc.). */
export function openModal(content: HTMLElement, options: OpenModalOptions): void {
  if (!host) return;
  closeModal();

  const previouslyFocused = document.activeElement as HTMLElement | null;
  const titleId = `modal-title-${Date.now()}`;

  const closeBtn = el("button", {
    className: "modal-panel__close",
    "aria-label": "Close",
    onclick: () => closeModal(),
  }, ["\u00d7"]);

  const panel = el(
    "div",
    {
      className: "modal-panel",
      role: "dialog",
      "aria-modal": "true",
      "aria-labelledby": titleId,
      tabIndex: -1,
    },
    [
      el("div", { className: "modal-panel__header" }, [
        el("div", { className: "modal-panel__title", id: titleId }, [options.title]),
        closeBtn,
      ]),
      el("div", { className: "modal-panel__body" }, [content]),
    ]
  );

  const onKeydown = (ev: KeyboardEvent) => {
    if (ev.key === "Escape") {
      ev.preventDefault();
      closeModal();
      return;
    }
    if (ev.key === "Tab") {
      const focusable = getFocusable(panel);
      if (!focusable.length) {
        ev.preventDefault();
        return;
      }
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (ev.shiftKey) {
        if (document.activeElement === first) {
          ev.preventDefault();
          last.focus();
        }
      } else {
        if (document.activeElement === last) {
          ev.preventDefault();
          first.focus();
        }
      }
    }
  };

  const overlay = el(
    "div",
    {
      className: "overlay overlay--modal",
      onclick: (ev: MouseEvent) => {
        if (ev.target === overlay) closeModal();
      },
    },
    [panel]
  );

  document.addEventListener("keydown", onKeydown);
  closeCurrent = () => {
    overlay.remove();
    document.removeEventListener("keydown", onKeydown);
    closeCurrent = null;
    // Restore focus to the element that was active before the modal opened.
    previouslyFocused?.focus();
  };

  host.append(overlay);

  // Set initial focus to the close button; fall back to the panel itself.
  closeBtn.focus();
}

export function closeModal(): void {
  closeCurrent?.();
}
